import type {
  GroupField,
  FormField,
  FormRowField,
  FormComponentType,
} from '@components/pages/FormBuilder/types'
import type {
  SchemaFormFieldValue,
  SchemaFormFieldValues,
} from './SchemaFormRenderer.component'

export type SchemaFormValidationResult = {
  isValid: boolean
  errorsByFieldId: Record<string, string>
  firstErrorFieldId: string | null
}

type FieldLike = FormField | GroupField | FormRowField

const SIGNATURE_FIELD_TYPES: ReadonlySet<FormComponentType> =
  new Set<FormComponentType>(['Signature Field', 'Dentist/Admin Signature'])

const isDataUrl = (value: string | unknown): boolean =>
  Boolean(value && typeof value === 'string' && value.startsWith('data:'))

const isGroupField = (item: FieldLike): item is GroupField =>
  'fields' in item &&
  'name' in item &&
  !('type' in item && (item as FormRowField).type === 'Form Row')

const isFormRowField = (item: FieldLike): item is FormRowField =>
  'type' in item &&
  (item as FormRowField).type === 'Form Row' &&
  'fields' in item

function normalizeLabel(field: FormField): string {
  const label = (field.label ?? '').trim()
  return label.length > 0 ? label : field.type
}

function isFieldRequired(field: FormField): boolean {
  return Boolean(field.required) || SIGNATURE_FIELD_TYPES.has(field.type)
}

function hasRequiredValue(
  field: FormField,
  rawValue: SchemaFormFieldValue | undefined,
): boolean {
  switch (field.type) {
    case 'Checkbox':
      return rawValue === true
    case 'Checkbox Group':
      return Array.isArray(rawValue) && rawValue.length > 0
    case 'Gender Selection':
    case 'Radio Group':
    case 'Dropdown':
      return typeof rawValue === 'string' && rawValue.trim().length > 0
    case 'Signature Field':
    case 'Dentist/Admin Signature': {
      if (typeof rawValue !== 'string') return false
      return isDataUrl(rawValue)
    }
    case 'Attachments':
      // Currently not captured in fieldValues (renderer is display-only).
      // If a future implementation stores attachments, treat non-empty string as filled.
      if (typeof rawValue !== 'string') return false
      return rawValue.trim().length > 0
    case 'Input Field':
    case 'Text Area':
    case 'Date Field':
    case 'Rich Text':
    default:
      if (typeof rawValue !== 'string') return false
      return rawValue.trim().length > 0
  }
}

function requiredMessage(field: FormField): string {
  if (SIGNATURE_FIELD_TYPES.has(field.type)) return 'Signature is required.'
  return `${normalizeLabel(field)} is required.`
}

function collectFields(items: FieldLike[]): FormField[] {
  const out: FormField[] = []
  for (const item of items) {
    if (isGroupField(item)) {
      out.push(...collectFields(item.fields))
    } else if (isFormRowField(item)) {
      out.push(...collectFields(item.fields))
    } else {
      out.push(item as FormField)
    }
  }
  return out
}

export function validateSchemaFormSubmission(
  groups: GroupField[],
  fieldValues: SchemaFormFieldValues,
): SchemaFormValidationResult {
  const errorsByFieldId: Record<string, string> = {}

  const allFields = groups.flatMap((g) => collectFields(g.fields))
  for (const field of allFields) {
    if (!isFieldRequired(field)) continue
    const raw = fieldValues[field.id]
    if (!hasRequiredValue(field, raw)) {
      errorsByFieldId[field.id] = requiredMessage(field)
    }
  }

  const firstErrorFieldId = Object.keys(errorsByFieldId)[0] ?? null
  return {
    isValid: firstErrorFieldId == null,
    errorsByFieldId,
    firstErrorFieldId,
  }
}
